import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useMissionControlStore } from '../store.js';
import type { Lane } from '../types.js';

interface VoiceComposerProps {
  workspaceId: string | null;
  lanes: Lane[];
}

type RecordingState = 'idle' | 'listening' | 'transcribing' | 'error';

function pickRecognition(): unknown {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { webkitSpeechRecognition?: unknown; SpeechRecognition?: unknown };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
}

export function VoiceComposer({ workspaceId, lanes }: VoiceComposerProps) {
  const emitEvent = useMissionControlStore((s) => s.emitEvent);
  const appendLaneLog = useMissionControlStore((s) => s.appendLaneLog);
  const setLaneStatus = useMissionControlStore((s) => s.setLaneStatus);
  const requestApproval = useMissionControlStore((s) => s.requestApproval);
  const activeLaneId = useMissionControlStore((s) => s.activeLaneId);

  const [transcript, setTranscript] = useState('');
  const [targetMode, setTargetMode] = useState<'coordinator' | 'lane' | 'broadcast'>('coordinator');
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const voiceSupported = Boolean(pickRecognition());

  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
    };
  }, []);

  function handleStartRecording() {
    const Recognition = pickRecognition() as (new () => SpeechRecognitionLike) | null;
    if (!Recognition) {
      setRecordingState('error');
      return;
    }
    const rec = new Recognition();
    rec.lang = 'en-US';
    rec.interimResults = false;
    rec.continuous = false;
    rec.onresult = (event) => {
      const last = event.results[event.results.length - 1];
      const text = last?.[0]?.transcript ?? '';
      if (text) {
        setTranscript((current) => (current ? `${current} ${text}` : text));
      }
    };
    rec.onend = () => setRecordingState('idle');
    rec.onerror = () => setRecordingState('error');
    recognitionRef.current = rec;
    rec.start();
    setRecordingState('listening');
  }

  function handleStopRecording() {
    recognitionRef.current?.stop();
    setRecordingState('idle');
  }

  function handleDispatch(event: React.FormEvent) {
    event.preventDefault();
    const text = transcript.trim();
    if (!text || !workspaceId) return;

    const coordinator = lanes.find((lane) => lane.role === 'coordinator');
    const targetLane =
      targetMode === 'lane'
        ? (lanes.find((lane) => lane.id === activeLaneId) ?? coordinator ?? lanes[0])
        : targetMode === 'coordinator'
          ? (coordinator ?? lanes[0])
          : null;

    if (targetMode === 'broadcast') {
      for (const lane of lanes) {
        appendLaneLog(lane.id, { text: `Voice: ${text}`, tone: 'neutral' });
        setLaneStatus(lane.id, 'running');
      }
    } else if (targetLane) {
      appendLaneLog(targetLane.id, { text: `Voice: ${text}`, tone: 'neutral' });
      setLaneStatus(targetLane.id, 'running');
    }

    emitEvent({
      workspaceId,
      laneId: targetLane?.id,
      kind: 'voice_dispatch',
      summary: `Voice dispatched to ${targetMode}: ${text.slice(0, 60)}`,
    });

    if (targetLane && (targetLane.role === 'builder' || targetLane.role === 'coordinator')) {
      requestApproval({
        workspaceId,
        laneId: targetLane.id,
        title: 'Act on voice prompt',
        risk: 'low',
        command: `# voice request\n${text}`,
        summary: `${targetLane.name} wants to act on: ${text.slice(0, 80)}`,
      });
    }

    setTranscript('');
  }

  const placeholder = voiceSupported
    ? 'Hold the mic and speak, or type your goal here.'
    : 'Type your goal here (voice capture unavailable in this browser).';

  return (
    <form className="panel voice-composer" onSubmit={handleDispatch} aria-label="Voice composer">
      <div className="section-heading">
        <span className="section-kicker">Composer</span>
        <strong>Voice to coordinator, or direct to a lane</strong>
      </div>

      <textarea
        className="composer-input"
        value={transcript}
        onChange={(event) => setTranscript(event.target.value)}
        placeholder={placeholder}
        aria-label="Voice transcript"
        rows={3}
      />

      <div className="composer-toolbar">
        <div className="composer-target" role="radiogroup" aria-label="Dispatch target">
          {(['coordinator', 'lane', 'broadcast'] as const).map((mode) => (
            <label
              key={mode}
              className={`composer-target-button ${targetMode === mode ? 'composer-target-active' : ''}`}
            >
              <input
                type="radio"
                name="composer-target"
                value={mode}
                checked={targetMode === mode}
                onChange={() => setTargetMode(mode)}
                className="composer-target-radio"
              />
              <span>{mode}</span>
            </label>
          ))}
        </div>

        <div className="composer-actions">
          <button
            type="button"
            className={`ptt-button ${recordingState === 'listening' ? 'ptt-active' : ''}`}
            onClick={recordingState === 'listening' ? handleStopRecording : handleStartRecording}
            disabled={!voiceSupported}
            aria-pressed={recordingState === 'listening'}
            aria-label={recordingState === 'listening' ? 'Stop recording' : 'Start push-to-talk'}
          >
            {recordingState === 'listening' ? 'Stop' : 'Push to talk'}
          </button>
          <button
            type="submit"
            className="primary-button"
            disabled={!transcript.trim() || !workspaceId}
            aria-label="Dispatch transcript"
          >
            Dispatch
          </button>
        </div>
      </div>

      {recordingState === 'error' ? (
        <p className="composer-warning">Voice capture failed or is unavailable. Typing still works.</p>
      ) : null}
    </form>
  );
}
